import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SERVER_URL } from '../../constants';
import { useUser } from '@clerk/clerk-react';
import { toast, ToastContainer } from 'react-toastify';
import {
  ShoppingCart, Star, ChevronLeft, ChevronRight, Tag,
  Package, Heart, Share2, Truck, Shield, Clock, PlayCircle
} from 'lucide-react';
import { ClipLoader } from 'react-spinners';
import CommentSection from './CommentSection';

const ProductDetails = () => {
  const { productId } = useParams();
  const { user } = useUser();
  const [product, setProduct] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState({ type: 'image', index: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState({ size: '', color: '' });
  const [featuredItems, setFeaturedItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [productId]);

  useEffect(() => {
    const fetchProductAndFeaturedItems = async () => {
      try {
        // Fetch current product
        const productResponse = await axios.get(`${SERVER_URL}/api/v1/product/${productId}`);
        const productData = productResponse.data.data;
        setProduct(productData);

        if (productData?.variations?.length > 0) {
          setSelectedVariation({
            size: productData.variations[0].size,
            color: productData.variations[0].color
          });
        }

        // Fetch all products to filter featured items dynamically based on current product's 'top' category
        if (productData?.top) {
          const allProductsResponse = await axios.get(`${SERVER_URL}/api/v1/product`);
          const { data } = allProductsResponse.data;
          const featured = data
            .filter((p) => p.top === productData.top && p._id !== productId) // Exclude current product
            .slice(0, 4); // Limit to 4 items
          setFeaturedItems(featured);
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to fetch product details.');
        setLoading(false);
      }
    };
    fetchProductAndFeaturedItems();
  }, [productId]);

  const addCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }

    if (product?.variations?.length > 0 && (!selectedVariation.size || !selectedVariation.color)) {
      toast.warning('Please select size and color');
      return;
    }

    try {
      const cartData = {
        userId: user.id,
        productId: productId,
        name: product?.name,
        price: product?.price,
        image: product?.imgUrl?.[0] || '',
        quantity: 1,
        variation: selectedVariation,
        totalAmount: product?.price || 0,
      };

      const response = await axios.post(`${SERVER_URL}/api/v1/cart/add`, cartData);
      if (response.data.m === 's') {
        toast.success('Product added to cart successfully!');
      } else if (response.data.m === 'f') {
        toast.warn("Product already added to Cart");
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      toast.error('Failed to add product to cart.');
    }
  };

  const addToFavourites = async () => {
    if (!user) {
      toast.error('Please login to add items to favourites');
      return;
    }

    if (product?.variations?.length > 0 && (!selectedVariation.size || !selectedVariation.color)) {
      toast.warning('Please select size and color');
      return;
    }

    try {
      const favData = {
        userId: user.id,
        productId: productId,
        name: product?.name,
        price: product?.price,
        image: product?.imgUrl?.[0] || '',
        quantity: 1,
        variation: selectedVariation,
        totalAmount: product?.price || 0,
      };

      const response = await axios.post(`${SERVER_URL}/api/v1/favi/add`, favData);
      if (response.data.m === 's') {
        toast.success('Product added to Favourites!');
      } else if (response.data.m === 'f') {
        toast.warn("Product already added to Favourites");
      }
    } catch (error) {
      console.error('Failed to add to favourites:', error);
      toast.error('Failed to add product to favourites.');
    }
  };

  const getMediaItems = () => {
    const images = product?.imgUrl?.map((url, index) => ({
      type: 'image',
      url,
      index
    })) || [];
    const videos = product?.videoUrl?.map((url, index) => ({
      type: 'video',
      url,
      index
    })) || [];
    return [...images, ...videos];
  };

  const mediaItems = getMediaItems();

  const nextMedia = () => {
    setSelectedMedia((prev) => {
      const newIndex = (prev.index + 1) % mediaItems.length;
      return { type: mediaItems[newIndex].type, index: newIndex };
    });
  };

  const prevMedia = () => {
    setSelectedMedia((prev) => {
      const newIndex = (prev.index - 1 + mediaItems.length) % mediaItems.length;
      return { type: mediaItems[newIndex].type, index: newIndex };
    });
  };

  const renderVariationOptions = () => {
    if (!product?.variations?.length) return null;

    const uniqueSizes = [...new Set(product.variations.map(v => v.size))];
    const uniqueColors = [...new Set(product.variations.map(v => v.color))];

    return (
      <div className="space-y-4 border-t border-gray-100 pt-4">
        {uniqueSizes.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Size</label>
            <div className="flex flex-wrap gap-2">
              {uniqueSizes.map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedVariation(prev => ({ ...prev, size }))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                    ${selectedVariation.size === size
                      ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        )}

        {uniqueColors.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Color</label>
            <div className="flex flex-wrap gap-2">
              {uniqueColors.map((color) => (
                <button
                  key={color}
                  onClick={() => setSelectedVariation(prev => ({ ...prev, color }))}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                    ${selectedVariation.color === color
                      ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStars = (rating = 0) => {
    const stars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center">
        <div className="flex items-center text-yellow-400">
          {[...Array(stars)].map((_, index) => (
            <Star key={index} size={20} fill="currentColor" />
          ))}
          {halfStar && <Star size={20} fill="currentColor" className="opacity-50" />}
        </div>
        <span className="ml-2 text-sm font-medium text-gray-600">
          {rating.toFixed(1)} ({product?.reviewCount || 0} reviews)
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ClipLoader size={150} color="#2563eb" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg">
          <div className="text-red-500 text-xl font-semibold mb-4">{error}</div>
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="min-h-screen bg-gray-50 pt-0 py-4 sm:py-6">
        <div className="container mx-auto px-4 max-w-7xl">
          <nav className="mb-4 sm:mb-6">
            <ol className="flex flex-wrap items-center gap-2 text-sm">
              <li>
                <button onClick={() => navigate('/')} className="text-gray-500 hover:text-blue-600">
                  Home
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li>
                <button onClick={() => navigate(`/category/${product?.category}`)} className="text-gray-500 hover:text-blue-600">
                  {product?.category}
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-medium">{product?.name}</li>
            </ol>
          </nav>

          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
              {/* Media Gallery (Images + Videos) */}
              <div className="space-y-4">
                <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                  {selectedMedia.type === 'image' ? (
                    <img
                      src={mediaItems[selectedMedia.index]?.url}
                      alt={product?.name}
                      className="object-cover w-full h-full transform transition-transform duration-500 hover:scale-105"
                    />
                  ) : (
                    <video
                      src={mediaItems[selectedMedia.index]?.url}
                      controls
                      autoPlay
                      className="object-contain w-full h-full"
                    />
                  )}
                  <button
                    className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg hover:bg-white transition-all"
                    onClick={prevMedia}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white/90 p-2 rounded-full shadow-lg hover:bg-white transition-all"
                    onClick={nextMedia}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>

                <div className="grid grid-cols-4 gap-2 sm:gap-4">
                  {mediaItems.map((media, idx) => (
                    <button
                      key={`${media.type}-${media.index}`}
                      onClick={() => setSelectedMedia({ type: media.type, index: idx })}
                      className={`relative aspect-square rounded-lg overflow-hidden
                        ${selectedMedia.index === idx && selectedMedia.type === media.type
                          ? 'ring-2 ring-blue-600 ring-offset-2'
                          : 'hover:opacity-80'
                        }`}
                    >
                      {media.type === 'image' ? (
                        <img
                          src={media.url}
                          alt={`Product view ${media.index + 1}`}
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <div className="relative w-full h-full">
                          <video
                            src={media.url}
                            className="object-cover w-full h-full"
                            muted
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <PlayCircle size={24} className="text-white" />
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Product Info */}
              <div className="flex flex-col space-y-4 sm:space-y-6">
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
                    {product?.name}
                  </h1>
                  <div className="flex items-center gap-3 flex-wrap">
                    {renderStars(product?.averageReview || 4.5)}
                    <span className="text-xs sm:text-sm text-gray-500">
                      Product Code: {product?.productCode || 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-2xl sm:text-3xl font-bold text-blue-600">
                    ₹{product?.price?.toFixed(2)}
                  </span>
                  {product?.originalPrice && (
                    <span className="text-lg sm:text-xl text-gray-400 line-through">
                      ₹{product?.originalPrice?.toFixed(2)}
                    </span>
                  )}
                  {product?.discount && (
                    <span className="text-xs sm:text-sm font-semibold text-green-500 bg-green-50 px-3 py-1 rounded-full">
                      {product?.discount}% OFF
                    </span>
                  )}
                </div>

                {renderVariationOptions()}

                <div className="space-y-4 border-t border-b py-4 sm:py-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="flex items-center gap-2">
                      <Truck className="text-blue-600" size={20} />
                      <div>
                        <p className="font-medium text-sm sm:text-base">Free Shipping</p>
                        <p className="text-xs sm:text-sm text-gray-500">On orders over ₹500</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="text-blue-600" size={20} />
                      <div>
                        <p className="font-medium text-sm sm:text-base">1 Year Warranty</p>
                        <p className="text-xs sm:text-sm text-gray-500">Full coverage</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="text-blue-600" size={20} />
                      <div>
                        <p className="font-medium text-sm sm:text-base">Fast Delivery</p>
                        <p className="text-xs sm:text-sm text-gray-500">3-5 business days</p>
                      </div>
                    </div>
                    {product?.totalStock && (
                      <div className="flex items-center gap-2">
                        <Package className="text-blue-600" size={20} />
                        <div>
                          <p className="font-medium text-sm sm:text-base">Stock Available</p>
                          <p className="text-xs sm:text-sm text-gray-500">{product.totalStock} units</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-base sm:text-lg text-gray-900">Product Features</h3>
                  </div>

                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{product?.description}</p>

                  <div className="flex flex-wrap gap-2">
                    {product?.features?.map((feature, index) => (
                      <span
                        key={index}
                        className="text-xs sm:text-sm bg-gray-50 text-gray-700 px-3 py-1 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={addCart}
                  className="w-full bg-red-600 text-white py-4 px-10 rounded-xl hover:bg-red-700 
                    transition-all flex items-center justify-center gap-2 font-semibold 
                    shadow-lg hover:shadow-xl focus:ring-4 focus:ring-red-200 text-base
                    tracking-wider transform active:translate-y-1"
                >
                  <ShoppingCart size={18} />
                  Add to Cart
                </button>

                <button
                  onClick={addToFavourites}
                  className="w-full bg-red-600 text-white py-4 px-10 rounded-xl hover:bg-red-700 
                    transition-all flex items-center justify-center gap-2 font-semibold 
                    shadow-lg hover:shadow-xl focus:ring-4 focus:ring-red-200 text-base
                    tracking-wider transform active:translate-y-1"
                >
                  <Heart size={18} />
                  Add to Favourites
                </button>
              </div>
            </div>
          </div>

          {/* Featured Items */}
          {featuredItems.length > 0 && (
            <div className="mt-10 sm:mt-12 lg:mt-16">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8">
                More {product?.top ? product.top.replace('top', '') : 'Featured'} Items
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {featuredItems.map((item, index) => (
                  <div
                    key={item._id}
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden group cursor-pointer"
                    onClick={() => navigate(`/product/${item._id}`)}
                  >
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={item.imgUrl[0] || '/api/placeholder/300/300'}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3 sm:p-4">
                      <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2 text-sm sm:text-base">
                        {item.name}
                      </h3>
                      <div className="flex items-baseline gap-2">
                        <span className="text-base sm:text-lg font-bold text-blue-600">
                          ₹{item.price?.toFixed(2)}
                        </span>
                        {item.discount && (
                          <span className="text-xs sm:text-sm text-green-500">-{item.discount}%</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <CommentSection productId={productId} user={user} />
    </>
  );
};

export default ProductDetails;
